// Localização: ./frontend/src/pages/Auth/InitialLoadingPage.jsx
// Página interativa de carregamento inicial após login

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Loader2, 
    Heart, 
    Calendar, 
    FileText, 
    DollarSign, 
    Users, 
    Clock,
    HelpCircle,
    Lightbulb,
    ChevronRight,
    CheckCircle2
} from 'lucide-react';

const InitialLoadingPage = () => {
    const [currentTipIndex, setCurrentTipIndex] = useState(0);
    const [expandedFaq, setExpandedFaq] = useState(null);

    // Dicas rotativas sobre a aplicação
    const tips = [
        {
            icon: Heart,
            title: 'Gestão de Pacientes',
            description: 'Gerencie todos os dados dos seus pacientes em um só lugar, com prontuários completos e histórico de cuidados.'
        },
        {
            icon: Calendar,
            title: 'Agendamentos Inteligentes',
            description: 'Organize visitas e consultas com nosso sistema de agendamento que evita conflitos e otimiza sua agenda.'
        },
        {
            icon: FileText,
            title: 'Diário de Cuidados',
            description: 'Registre sinais vitais, medicações e observações importantes diretamente no aplicativo.'
        },
        {
            icon: DollarSign,
            title: 'Controle Financeiro',
            description: 'Acompanhe receitas, despesas e tenha uma visão completa da saúde financeira da sua organização.'
        },
        {
            icon: Users,
            title: 'Gestão de Equipe',
            description: 'Gerencie profissionais, permissões e acompanhe o desempenho da sua equipe de cuidados.'
        },
        {
            icon: Clock,
            title: 'Registro de Ponto',
            description: 'Controle de ponto digital com geolocalização para garantir transparência e precisão.'
        }
    ];

    // Perguntas frequentes
    const faqs = [
        {
            question: 'Como adicionar um novo paciente?',
            answer: 'Acesse a seção "Pacientes" no menu lateral e clique em "Novo Paciente". Preencha os dados solicitados e salve.'
        },
        {
            question: 'Como criar um agendamento?',
            answer: 'Vá até "Agendamentos" e clique em "Novo Agendamento". Selecione o paciente, profissional, data e horário desejados.'
        },
        {
            question: 'Como registrar cuidados no diário?',
            answer: 'Na página "Diário de Cuidados", clique no botão flutuante "+" e escolha o tipo de registro que deseja fazer.'
        },
        {
            question: 'Como gerenciar permissões de usuários?',
            answer: 'Acesse "Configurações" > "Permissões" para definir quais recursos cada usuário pode acessar.'
        },
        {
            question: 'Como visualizar relatórios financeiros?',
            answer: 'No menu "Financeiro", você encontrará dashboards e relatórios detalhados sobre receitas e despesas.'
        }
    ];

    // Rotacionar dicas a cada 4 segundos
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTipIndex((prev) => (prev + 1) % tips.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [tips.length]);

    const currentTip = tips[currentTipIndex];
    const TipIcon = currentTip.icon;

    return (
        <div className="min-h-screen bg-gradient-to-br from-fundo-cinza-moderno via-white to-higeia-light flex items-center justify-center p-4">
            <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Coluna Principal - Conteúdo Interativo */}
                <div className="lg:col-span-2 space-y-6">
                    
                    {/* Card de Dica Rotativa */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="bg-white rounded-2xl shadow-soft p-6 border border-gray-100"
                    >
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-higeia-primary to-higeia-medium rounded-xl flex items-center justify-center">
                                <TipIcon className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={currentTipIndex}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 20 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <div className="flex items-center gap-2 mb-2">
                                            <Lightbulb className="w-4 h-4 text-amber-500" />
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                {currentTip.title}
                                            </h3>
                                        </div>
                                        <p className="text-gray-600 leading-relaxed">
                                            {currentTip.description}
                                        </p>
                                    </motion.div>
                                </AnimatePresence>
                                
                                {/* Indicadores de dicas */}
                                <div className="flex gap-1.5 mt-4">
                                    {tips.map((_, index) => (
                                        <div
                                            key={index}
                                            className={`h-1.5 rounded-full transition-all duration-300 ${
                                                index === currentTipIndex
                                                    ? 'bg-higeia-medium w-8'
                                                    : 'bg-gray-200 w-1.5'
                                            }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Grid de Funcionalidades */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="grid grid-cols-2 md:grid-cols-3 gap-4"
                    >
                        {tips.map((tip, index) => {
                            const Icon = tip.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                                    className="bg-white rounded-xl p-4 border border-gray-100 hover:border-higeia-medium hover:shadow-md transition-all duration-300 cursor-pointer group"
                                >
                                    <div className="w-10 h-10 bg-higeia-light rounded-lg flex items-center justify-center mb-3 group-hover:bg-higeia-medium transition-colors">
                                        <Icon className="w-5 h-5 text-higeia-medium group-hover:text-white transition-colors" />
                                    </div>
                                    <h4 className="text-sm font-semibold text-gray-900 mb-1">
                                        {tip.title}
                                    </h4>
                                </motion.div>
                            );
                        })}
                    </motion.div>

                    {/* Seção de FAQ */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="bg-white rounded-2xl shadow-soft p-6 border border-gray-100"
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <HelpCircle className="w-5 h-5 text-higeia-medium" />
                            <h3 className="text-lg font-semibold text-gray-900">
                                Perguntas Frequentes
                            </h3>
                        </div>
                        <div className="space-y-2">
                            {faqs.map((faq, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.3 + index * 0.1 }}
                                    className="border border-gray-100 rounded-lg overflow-hidden"
                                >
                                    <button
                                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                                        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                                    >
                                        <span className="text-sm font-medium text-gray-900 pr-4">
                                            {faq.question}
                                        </span>
                                        <ChevronRight
                                            className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
                                                expandedFaq === index ? 'rotate-90' : ''
                                            }`}
                                        />
                                    </button>
                                    <AnimatePresence>
                                        {expandedFaq === index && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                                className="overflow-hidden"
                                            >
                                                <div className="px-4 pb-3 text-sm text-gray-600">
                                                    {faq.answer}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* Coluna Lateral - Indicador de Carregamento */}
                <div className="lg:col-span-1">
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className="bg-white rounded-2xl shadow-soft p-6 border border-gray-100 sticky top-6"
                    >
                        {/* Logo e Título */}
                        <div className="text-center mb-6">
                            <div className="flex justify-center mb-3">
                                <img
                                    src="/images/higeia-icon-only.png"
                                    alt="Higeia.tech"
                                    className="w-16 h-16 object-contain"
                                />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-1">
                                Carregando
                            </h2>
                            <p className="text-sm text-gray-500">
                                Preparando sua experiência
                            </p>
                        </div>

                        {/* Indicador de Carregamento Pequeno */}
                        <div className="flex flex-col items-center justify-center py-8">
                            <Loader2 className="w-8 h-8 text-higeia-medium animate-spin mb-4" />
                            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                <motion.div
                                    className="h-full bg-gradient-to-r from-higeia-primary to-higeia-medium"
                                    initial={{ width: '0%' }}
                                    animate={{ width: '100%' }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: 'easeInOut'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Lista de Verificação */}
                        <div className="space-y-3 mt-6 pt-6 border-t border-gray-100">
                            <div className="flex items-center gap-3 text-sm">
                                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                                <span className="text-gray-600">Autenticação concluída</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                                <Loader2 className="w-4 h-4 text-higeia-medium animate-spin flex-shrink-0" />
                                <span className="text-gray-600">Carregando permissões</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                                <Loader2 className="w-4 h-4 text-higeia-medium animate-spin flex-shrink-0" />
                                <span className="text-gray-600">Preparando dados</span>
                            </div>
                        </div>

                        {/* Mensagem Motivacional */}
                        <div className="mt-6 pt-6 border-t border-gray-100">
                            <p className="text-xs text-center text-gray-500 italic">
                                "Cuidar bem é nossa missão"
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default InitialLoadingPage;

